import React, { useEffect, useState } from "react";
import ApiService from "../../network/ApiService";
import AuthService from "../../network/AuthService";
import ConversationDetail from "./ConversationDetail";
import styles from "./inbox.module.css";

const NewConversationModal = ({ teamId, onClose, onCreated }) => {
  const [selected, setSelected] = useState("");
  const options = ["Strafsenat", "Buchhaltung", "Allgemeines", "Ligabetrieb"];

  const startNewConversation = async () => {
    if (!teamId || !selected.trim()) return;

    try {
      const conversation = {
        team: { id: teamId },
        subject: selected,
        messages: [],
        open: true,
      };

      const response = await new ApiService().post("chat/", conversation);
      onClose();
      onCreated(response.id);
    } catch (error) {
      console.error("Failed to start new conversation:", error);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2>Neue Nachricht Verfassen</h2>

        <div style={{ margin: "25px 0" }}>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "6px",
              fontSize: "16px",
            }}
          >
            <option value="" disabled>
              Bitte Betreff auswählen
            </option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "20px",
          }}
        >
          <button
            onClick={startNewConversation}
            disabled={!selected}
            style={{
              backgroundColor: "orange",
              padding: "10px 20px",
              borderRadius: "6px",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              flex: 1,
            }}
          >
            Abschicken
          </button>
          <button
            onClick={onClose}
            style={{
              backgroundColor: "orange",
              padding: "10px 20px",
              borderRadius: "6px",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              flex: 1,
            }}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};

const ConversationList = () => {
  const [messages, setMessages] = useState([]);
  const [team, setTeam] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const authService = new AuthService();
  const apiService = new ApiService();

  const getLastMessage = (conv) => conv.messages?.[conv.messages.length - 1];

  const sortConversations = (convs) => {
    return [...convs].sort((a, b) => {
      const dateA = new Date(getLastMessage(a)?.created || 0);
      const dateB = new Date(getLastMessage(b)?.created || 0);
      return dateB - dateA;
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const day = dateString.slice(8, 10);
    const month = dateString.slice(5, 7);
    const time = dateString.slice(11, 16);
    return `${day}.${month} - ${time}`;
  };


  const fetchMessages = async (reorderOnly = false) => {
    try {
      const teamID = authService.getTeamID();
      const authToken = authService.getAuthToken();

      if (teamID && authToken) {
        const response = await apiService.get(`chat/team/${teamID}`);

        if (!reorderOnly) {
          setMessages(sortConversations(response));
          setTeam(teamID);
          return;
        }

        const existingTimestamps = messages.reduce((acc, conv) => {
          acc[conv.id] = getLastMessage(conv)?.created || 0;
          return acc;
        }, {});

        const updated = sortConversations(response);

        const needsUpdate = updated.some(
          (conv) =>
            existingTimestamps[conv.id] !== getLastMessage(conv)?.created
        );

        if (needsUpdate) {
          setMessages(updated);
        }
      } else {
        console.error("TeamID or AuthToken is missing.");
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(() => fetchMessages(true), 60000);
    return () => clearInterval(interval);
  }, []);

  const handleClick = (id) => {
    setSelectedConversationId(id);
  };

  if (selectedConversationId) {
    return (
      <ConversationDetail
        id={selectedConversationId}
        onBack={() => setSelectedConversationId(null)}
      />
    );
  }

  return (
    <div className={styles.rechnungenList}>
      {team && (
        <div
          style={{
            backgroundColor: "#24292B",
            padding: "25px",
            marginBottom: "20px",
          }}
        >
          <div className={styles.guthaben}>
            Inbox
            <div className={styles.sub}>
              (Wir versuchen auf Nachrichten innerhalb von 24h zu antworten)
            </div>
            <div className={styles.sub}>
              <button
                style={{
                  backgroundColor: "orange",
                  padding: "10px",
                  borderRadius: "4px",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={() => setShowModal(true)}
              >
                Neue Nachricht Verfassen
              </button>
            </div>
          </div>
        </div>
      )}

      {[['Offene Konversationen', true], ['Geschlossene Konversationen', false]].map(
        ([title, isOpen]) => {
          const filtered = messages.filter((conv) => conv.open === isOpen);
          if (filtered.length === 0) return null;

          return (
            <div key={title} style={{ marginBottom: "40px" }}>
              <h4 style={{ padding: "10px 0", color: "white" }}>{title}</h4>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.header}>Betreff</th>
                    <th className={styles.header}>Datum</th>
                    <th className={styles.header}></th>
                    <th className={styles.header}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((conversation) => {
                    const latestMessage = getLastMessage(conversation);
                    const isUnread =
                      latestMessage &&
                      !latestMessage.read &&
                      latestMessage.sender_team === false;

                    return (
                      <tr key={conversation.id} className={styles.row}>
                        <td className={styles.cell}>
                          {isUnread && (
                            <span
                              style={{
                                display: "inline-block",
                                width: "10px",
                                height: "10px",
                                backgroundColor: "orange",
                                borderRadius: "50%",
                                marginRight: "8px",
                              }}
                            />
                          )}
                          {conversation.subject}
                        </td>
                        <td className={styles.cell}>
                          {latestMessage
                          ? formatDateTime(latestMessage.created)
                            : "-"}
                        </td>
                        <td className={styles.cell} style={{color: "orange", textDecoration: "underline"}}>
                          Status: {conversation.open ? "Offen" : "Geschlossen"}
                        </td>
                        <td className={styles.cell}>
                          <button
                            style={{
                              backgroundColor: "orange",
                              padding: "10px",
                              borderRadius: "4px",
                              color: "#fff",
                              border: "none",
                              cursor: "pointer",
                            }}
                            onClick={() => handleClick(conversation.id)}
                          >
                            Öffnen
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }
      )}

      {showModal && (
        <NewConversationModal
          teamId={team}
          onClose={() => setShowModal(false)}
          onCreated={(newId) => handleClick(newId)}
        />
      )}
    </div>
  );
};

export default ConversationList;
